import { useState, useEffect, useCallback } from "react";
import { useI18n } from "../../i18n";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Shield, Lock, Smartphone, Bell, ChevronLeft, ChevronRight, LayoutDashboard, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { usePricing } from "../../context/PricingContext";
import { useCMSContent } from "../../hooks/useCMSContent";

// Product showcase images - Car, Kids, Pets, Luggage
const HERO_IMAGES = [
  {
    id: 1,
    url: "https://customer-assets.emergentagent.com/job_canva-stickers/artifacts/27dohpu7_image.png",
    alt: "Auto con sticker QR AvisoCar",
  },
  {
    id: 2,
    url: "https://customer-assets.emergentagent.com/job_canva-stickers/artifacts/c93khhki_image.png",
    alt: "Niños con pulseras QR AvisoCar",
  },
  {
    id: 3,
    url: "https://customer-assets.emergentagent.com/job_canva-stickers/artifacts/1gtpg73k_image.png",
    alt: "Mascotas con placas QR AvisoCar",
  },
  {
    id: 4,
    url: "https://customer-assets.emergentagent.com/job_canva-stickers/artifacts/e8x4l7jy_image.png",
    alt: "Equipaje con stickers QR AvisoCar",
  },
];

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { user, isAuthenticated } = useAuth();
  const { data: cms } = useCMSContent("landing_json");
  const h = cms?.hero || {};
  const { priceLabelSemantic, planMonths, localizeText, chargeNote, approxHint } = usePricing();
  const { t: tt } = useI18n();

  const getGreeting = () => {
    if (!user || !user.full_name) return null;
    const firstName = user.full_name.split(" ")[0];
    const fem = h.logged_greeting_female || "Bienvenida";
    const mas = h.logged_greeting_male || "Bienvenido";
    const neu = h.logged_greeting_neutral || "Hola";
    if (user.sex === "female") return `${fem}, ${firstName}`;
    if (user.sex === "male") return `${mas}, ${firstName}`;
    return `${neu}, ${firstName}`;
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % HERO_IMAGES.length);
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + HERO_IMAGES.length) % HERO_IMAGES.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(goToNext, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, goToNext]);

  const bullets = [
    { icon: Lock, text: h.bullet_1 || "Tu número permanece privado" },
    { icon: Smartphone, text: h.bullet_2 || "Funciona desde el navegador" },
    { icon: Bell, text: h.bullet_3 || "Avisos reales" },
  ];

  return (
    <section className="relative overflow-hidden bg-[#0B2545]" data-testid="hero-section">
      {/* capas de profundidad: halo cian del escudo del logo + retícula QR muy tenue */}
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, #1B84FF 0%, rgba(11,37,69,0) 70%)" }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, #5BC0FF 0%, rgba(11,37,69,0) 70%)" }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #5BC0FF 1px, transparent 1px), linear-gradient(to bottom, #5BC0FF 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid items-center gap-16 lg:grid-cols-[1.05fr_1fr]">
          <div className="animate-rise">
            {isAuthenticated && getGreeting() && (
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5BC0FF]">
                  {getGreeting()}
                </p>
                <p className="mt-2 text-base text-slate-300 md:text-lg">
                  {h.logged_subtitle || "Tu protección AvisoCar te está esperando"}
                </p>
              </div>
            )}

            {!isAuthenticated && (
              <div
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#5BC0FF]/30 bg-[#5BC0FF]/10 px-4 py-2 text-sm font-medium text-[#9FD8FF]"
                data-testid="hero-badge"
              >
                <Shield className="h-4 w-4" />
                {localizeText(h.badge) ||
                  (priceLabelSemantic(6)
                    ? `${tt("pricing.from")} ${priceLabelSemantic(6)} · ${priceLabelSemantic(12)} ${tt("pricing.per_12m")}`
                    : "")}
              </div>
            )}

            <h1
              className="mb-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
              data-testid="hero-title"
            >
              {h.title || "Contacta sin mostrar tu número"}
            </h1>
            <p className="mb-8 max-w-xl text-base text-slate-300 md:text-lg" data-testid="hero-subtitle">
              {h.subtitle || "Autos, niños, mascotas y objetos protegidos con AvisoCar."}
            </p>

            <ul className="mb-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 backdrop-blur-sm"
                >
                  <b.icon className="h-4 w-4 shrink-0 text-[#5BC0FF]" />
                  <span className="text-sm text-white" data-testid={`hero-bullet-${i + 1}`}>{b.text}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button
                    className="h-auto w-full whitespace-normal rounded-full bg-[#1B84FF] px-8 py-6 text-base font-semibold text-white shadow-[0_12px_32px_rgba(27,132,255,0.35)] transition-transform hover:-translate-y-0.5 hover:bg-[#0F6FDB] sm:w-auto"
                    data-testid="hero-dashboard-btn"
                  >
                    <LayoutDashboard className="mr-2 h-5 w-5" />
                    {h.logged_cta || "Ir a mi Dashboard"}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register" className="sm:w-auto">
                    <Button
                      className="h-auto w-full whitespace-normal rounded-full bg-[#1B84FF] px-8 py-6 text-base font-semibold text-white shadow-[0_12px_32px_rgba(27,132,255,0.35)] transition-transform hover:-translate-y-0.5 hover:bg-[#0F6FDB] sm:w-auto"
                      data-testid="hero-buy-cta"
                    >
                      {(h.cta_primary || "Comprar tu QR ahora").split("—")[0].trim()} — {priceLabelSemantic(planMonths)}{" "}
                      {planMonths === 12 ? tt("pricing.per_12m") : tt("pricing.per_6m")}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <a
                    href="#como-funciona"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                    data-testid="hero-secondary-cta"
                  >
                    {tt("nav.how")}
                  </a>
                </>
              )}
            </div>

            {!isAuthenticated && (
              <div className="mt-5 space-y-1">
                <p className="text-sm text-slate-400" data-testid="hero-cta-note">
                  {planMonths === 12 ? tt("pricing.protection_12m") : tt("pricing.protection_6m")}
                </p>
                {chargeNote(planMonths) && (
                  <p className="max-w-lg text-xs leading-relaxed text-slate-400" data-testid="hero-charge-note">
                    {chargeNote(planMonths)} · {approxHint()}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Carrusel de producto */}
          <div
            className="relative animate-rise [animation-delay:120ms]"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="relative z-10 aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 bg-white shadow-[0_30px_70px_rgba(0,0,0,0.35)]">
              {HERO_IMAGES.map((image, index) => (
                <div
                  key={image.id}
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                    index === currentIndex ? "opacity-100" : "opacity-0"
                  }`}
                  aria-hidden={index !== currentIndex}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="h-full w-full bg-white object-contain"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </div>
              ))}

              <button
                onClick={goToPrev}
                className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#0B2545]/70 text-white backdrop-blur-sm transition-transform hover:scale-110"
                aria-label="Anterior"
                data-testid="hero-carousel-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#0B2545]/70 text-white backdrop-blur-sm transition-transform hover:scale-110"
                aria-label="Siguiente"
                data-testid="hero-carousel-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 flex justify-center gap-2">
              {HERO_IMAGES.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex ? "w-8 bg-[#5BC0FF]" : "w-2 bg-white/30 hover:bg-white/50"
                  }`}
                  aria-label={`Ir a imagen ${index + 1}`}
                  data-testid={`hero-carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
